<script setup>
import { computed } from 'vue';
import { Handle, Position, useNode } from '@vue-flow/core';
import { useI18n } from 'vue-i18n';
import { useNodeAudience } from '../../composables/useNodeAudience.js';
import { NODE_CONFIG } from '../../constants/nodeTypes.js';

const props = defineProps({
  type: { type: String, default: 'datatable_query' },
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const { id } = useNode();
const audienceCount = useNodeAudience(id);

const config = computed(() => NODE_CONFIG[props.type] ?? NODE_CONFIG.datatable_query);
const hasFoundHandle = computed(() => props.type === 'datatable_query');
</script>

<template>
  <div
    class="flow-node w-[180px] rounded-xl overflow-hidden shadow-md border-2 transition-all"
    :class="selected ? 'border-green-400' : 'border-transparent'"
  >
    <Handle type="target" :position="Position.Top" />

    <div class="flex items-center gap-2 px-3 py-2 bg-green-500 text-white">
      <span :class="[config.icon, 'size-4 shrink-0']" />
      <span class="text-xs font-semibold truncate flex-1">{{ data.name || (config.label ? t(config.label) : '') }}</span>
      <span
        v-if="audienceCount !== null"
        class="text-[9px] bg-white/20 rounded-full px-1.5 py-0.5 font-medium tabular-nums shrink-0"
      >
        {{ audienceCount }}
      </span>
    </div>

    <div class="px-3 py-2 bg-n-solid-1 text-n-slate-12 text-xs">
      <span v-if="data.table_slug" class="text-n-slate-11 truncate block">{{ data.table_slug }}</span>
      <span v-else class="text-n-slate-9">{{ t('FLOW_BUILDER.NODES.DATATABLE.CONFIGURE') }}</span>
    </div>

    <template v-if="hasFoundHandle">
      <div class="bg-n-solid-1 flex justify-between px-[18%] pt-1 pb-3">
        <span class="text-[10px] text-n-slate-9">{{ t('FLOW_BUILDER.NODES.DATATABLE.FOUND') }}</span>
        <span class="text-[10px] text-n-slate-9">{{ t('FLOW_BUILDER.NODES.DATATABLE.NOT_FOUND') }}</span>
      </div>
      <Handle id="found" type="source" :position="Position.Bottom" class="!bg-n-teal-9 !border-n-teal-7" style="left: 25%" />
      <Handle id="not_found" type="source" :position="Position.Bottom" class="!bg-n-ruby-9 !border-n-ruby-7" style="left: 75%" />
    </template>
    <template v-else>
      <Handle type="source" :position="Position.Bottom" />
    </template>
  </div>
</template>
